import classNames from 'classnames';
import lodash from 'lodash';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import React, { useCallback, useContext, useMemo } from 'react';

import { ContentTraverserContext, RicosContext } from './../../../../../ricos-context';
import type { Node } from './../../../../../ricos-schema/legacy/wix/rich_content/v1';
import type { ImageData, LinkData, PluginContainerData } from './../../../../../ricos-schema/next-schema';
import type { NodeViewContainer } from './../../../../../ricos-types';

import { ErrorBoundary } from '../../../components/ErrorBoundary';
import Anchorable from '../../utils/Anchorable';
import { ContainerElement } from './ContainerElement';
import { alignmentClassName, sizeClassName, textWrapClassName } from './getClassNames';
import { LinkViewerWrapper } from './LinkViewerWrapper';
import classes from './plugin.rtlignore-scss';

const isDefined = (value?: string): value is string => value !== undefined;

const convertSpoilerData = (data: Partial<PluginContainerData> = {}) => {
  return {
    config: {
      spoiler: {
        enabled: !!data.spoiler?.enabled,
        description: data.spoiler?.description,
        buttonContent: data.spoiler?.buttonText,
      },
    },
  };
};

type ComponentData = ImageData & LinkData;

export const nodeViewContainer: NodeViewContainer = ({
  node,
  nodeIndex,
  customClassNames,
  withHorizontalScroll,
  children,
}) => {
  const { isMobile } = useContext(RicosContext);
  const { SpoilerViewerWrapper } = useContext(ContentTraverserContext);
  const dataProperty = `${lodash.camelCase(node.type)}Data`;
  const componentData = (node[dataProperty as $TSFixMeKey<Node>] || {}) as ComponentData;
  const { containerData, link } = componentData;
  const { alignment, size, textWrap } = customClassNames || {};

  const containerClassName = classNames(
    {
      [classes.pluginContainerMobile]: isMobile,
      [classes.horizontalScrollbar]: withHorizontalScroll,
    },
    isDefined(alignment) ? alignment : alignmentClassName(node),
    isDefined(size) ? size : sizeClassName(node),
    isDefined(textWrap) ? textWrap : textWrapClassName(node)
  );

  const renderContainerComponent = useCallback(
    (contentComponent: ReactNode) => (
      <ContainerElement
        className={containerClassName}
        nodeType={node.type}
        componentData={componentData}
        nodeIndex={nodeIndex}
      >
        {contentComponent}
      </ContainerElement>
    ),
    [
      containerClassName,
      node.type,
      containerData?.width?.custom,
      containerData?.width?.size,
      containerData?.alignment,
      nodeIndex,
    ]
  );

  const renderSpoilerWrapper = useCallback(
    (contentComponent: ReactNode): ReactElement => {
      if (SpoilerViewerWrapper) {
        return (
          <SpoilerViewerWrapper
            className={containerClassName}
            width={containerData?.width?.custom ? parseInt(containerData.width.custom) : undefined}
            type={node.type}
            componentData={convertSpoilerData(containerData)}
          >
            {contentComponent}
          </SpoilerViewerWrapper>
        );
      }

      return contentComponent as ReactElement;
    },
    [
      containerClassName,
      node.type,
      containerData?.width?.custom,
      containerData?.width?.size,
      containerData?.alignment,
      containerData?.spoiler?.enabled,
      containerData?.spoiler?.buttonText,
      containerData?.spoiler?.description,
    ]
  );

  const renderLinkWrapper = useCallback(
    (contentComponent: ReactNode): ReactElement => (
      <LinkViewerWrapper link={link} nodeType={node.type}>
        {contentComponent}
      </LinkViewerWrapper>
    ),
    [
      node.type,
      link?.url,
      link?.target,
      link?.anchor,
      link?.rel?.nofollow,
      link?.rel?.noreferrer,
      link?.rel?.sponsored,
    ]
  );

  if (typeof children === 'function') {
    return (
      <BaseContainer node={node}>
        {children(
          useMemo(
            () => ({
              ContainerElement: props => renderContainerComponent(props.children),
              SpoilerWrapper: props => renderSpoilerWrapper(props.children),
              LinkWrapper: props => renderLinkWrapper(props.children),
            }),
            [renderContainerComponent, renderSpoilerWrapper, renderLinkWrapper]
          )
        )}
      </BaseContainer>
    );
  }

  return (
    <BaseContainer node={node}>
      {renderLinkWrapper(renderSpoilerWrapper(renderContainerComponent(children)))}
    </BaseContainer>
  );
};

const BaseContainer: React.FC<PropsWithChildren & { node: Node }> = ({ children, node }) => {
  return (
    <ErrorBoundary>
      <Anchorable nodeId={node.id}>
        <div className={classes.atomic}>{children}</div>
      </Anchorable>
    </ErrorBoundary>
  );
};
